import React, { useEffect, useRef } from 'react';

/**
 * Animated WebGL-feel aurora background — pure CSS gradient orbs animated with
 * requestAnimationFrame so the blobs slowly drift, scale, and breathe.
 * Adds depth + life to dark hero without any heavy three.js.
 */
export function AuroraBackground({ intensity = 1, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const start = performance.now();

    const tick = (now) => {
      const t = (now - start) / 1000;
      const x1 = 50 + Math.sin(t * 0.18) * 18;
      const y1 = 30 + Math.cos(t * 0.22) * 14;
      const x2 = 70 + Math.cos(t * 0.16) * 22;
      const y2 = 65 + Math.sin(t * 0.20) * 18;
      const x3 = 30 + Math.sin(t * 0.14 + 1.2) * 16;
      const y3 = 80 + Math.cos(t * 0.18 + 2.3) * 14;
      el.style.setProperty('--ax1', `${x1}%`);
      el.style.setProperty('--ay1', `${y1}%`);
      el.style.setProperty('--ax2', `${x2}%`);
      el.style.setProperty('--ay2', `${y2}%`);
      el.style.setProperty('--ax3', `${x3}%`);
      el.style.setProperty('--ay3', `${y3}%`);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={'pointer-events-none absolute inset-0 overflow-hidden ' + className}
      style={{
        '--ax1': '50%', '--ay1': '30%',
        '--ax2': '70%', '--ay2': '65%',
        '--ax3': '30%', '--ay3': '80%',
        opacity: intensity,
      }}
    >
      <div
        className="absolute inset-0 blur-3xl"
        style={{
          background: `
            radial-gradient(45vw 45vw at var(--ax1) var(--ay1), hsl(226, 100%, 58% / 0.45), transparent 60%),
            radial-gradient(40vw 40vw at var(--ax2) var(--ay2), hsl(270, 100%, 65% / 0.30), transparent 65%),
            radial-gradient(35vw 35vw at var(--ax3) var(--ay3), hsl(150, 90%, 55% / 0.22), transparent 60%),
            radial-gradient(50vw 30vw at 50% 0%, hsl(320, 90%, 55% / 0.15), transparent 70%)
          `,
        }}
      />
      {/* Subtle noise overlay for depth */}
      <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.92' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
      }} />
      {/* Grid */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            'linear-gradient(hsl(220, 30%, 24% / 0.6) 1px, transparent 1px), linear-gradient(90deg, hsl(220, 30%, 24% / 0.6) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black, transparent 75%)',
        }}
      />
    </div>
  );
}

export default AuroraBackground;
